import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { IUserAddress } from '../models/customer.models';
import { Observable } from 'rxjs';
import { catchError, map, take, tap } from 'rxjs/operators';
import {
  IAddressData,
  IAddressesDataIncluded,
  ICCPaymentResponse,
  ICheckoutData,
  ICheckoutUpdate,
  ICustomAddressData,
  EPaymentProviderName,
  ISipayceCardPaymentData,
  ISipayceCardPaymentResponse,
} from '../models/checkout.models';
import { GlueConfigurationService } from './glue-configuration.service';
import { AddressUtils } from '../utils/address.utils';

@Injectable({
  providedIn: 'root',
})
export class DeliveryDetailsService {
  private readonly http: HttpClient = inject(HttpClient);
  private readonly glueConfiguration: GlueConfigurationService = inject(GlueConfigurationService);

  private readonly glueUrl: string = this.glueConfiguration.getEndpointUrl();

  private readonly httpParamsForGetCompanyUsersRoleItems: { params: HttpParams } = {
    params: new HttpParams()
      .set('include', 'company-roles,customers'),
  };

  private readonly httpParamsForGetApprovers: { params: HttpParams } = {
    params: new HttpParams()
      .set('include', 'customers'),
  };

  private static handleError(error: any): Promise<never> {
    return Promise.reject(error);
  }

  postSipayceCardPayment(data: ISipayceCardPaymentData): Observable<ISipayceCardPaymentResponse> {
    const url: string = this.glueUrl + `/sp-card-payment`;
    const newBody: string = JSON.stringify(data);

    return this.http.post<any>(url, newBody).pipe(
      catchError(DeliveryDetailsService.handleError),
    );
  }

  getCCAvenuePaymentData(orderId: string): Observable<ICCPaymentResponse> {
    const cartsInfoUrl: string = this.glueUrl + `/orders/${orderId}/cc-payment`;

    return this.http.get<any>(cartsInfoUrl).pipe(
      catchError(DeliveryDetailsService.handleError),
    );
  }

  postCheckoutUpdate(data: ICheckoutUpdate, guestUserId: string = ''): Observable<any> {
    const newBody: string = JSON.stringify(data);
    const cartsInfoUrl: string = this.glueUrl + '/checkout-update';

    if (guestUserId) {
      let headers: HttpHeaders = new HttpHeaders();
      headers = headers.set('X-Anonymous-Customer-Unique-Id', guestUserId);
      return this.http.post<any>(cartsInfoUrl, newBody, {headers}).pipe(
        catchError(DeliveryDetailsService.handleError),
      );
    } else {
      return this.http.post<any>(cartsInfoUrl, newBody).pipe(
        catchError(DeliveryDetailsService.handleError),
      );
    }
  }

  postCheckoutData(data: ICheckoutData, guestUserId: string = ''): Observable<any> {
    const newBody: string = JSON.stringify(data);
    const payments: any[] | undefined = data?.data?.attributes?.payments;
    const includeCarts: boolean = Array.isArray(payments) && payments.length > 0 && !!payments[0].paymentProviderName && payments[0].paymentProviderName === EPaymentProviderName.CC_AVENUE;
    const cartsInfoUrl: string = this.glueUrl + '/checkout-data' + (includeCarts ? '?include=carts' : '');

    if (guestUserId) {
      let headers: HttpHeaders = new HttpHeaders();
      headers = headers.set('X-Anonymous-Customer-Unique-Id', guestUserId);
      return this.http.post<any>(cartsInfoUrl, newBody, {headers}).pipe(
        catchError(DeliveryDetailsService.handleError),
      );
    } else {
      return this.http.post<any>(cartsInfoUrl, newBody).pipe(
        map((response: any) => response),
        catchError(DeliveryDetailsService.handleError),
      );
    }
  }

  getShipmentMethodPrice(cartId: string): Observable<any> {
    const cartsUrl: string = `${this.glueUrl}/carts/${cartId}?include=items`;

    return this.http.get<any>(cartsUrl).pipe(
      tap(),
      catchError(DeliveryDetailsService.handleError),
    );
  }

  //TODO: Set explicit type for the first parameter and return value.
  // Two different types are used for the parameter (ICheckout and ICheckoutInput), while they have almost the same structure.
  // Merge them together and set final type for the parameter.
  postCheckout(data: any, guestUserId: string = ''): Observable<any> {
    const newBody: string = JSON.stringify(data);
    const cartsInfoUrl: string = this.glueUrl + '/checkout';

    if (guestUserId) {
      let headers: HttpHeaders = new HttpHeaders();
      headers = headers.set('X-Anonymous-Customer-Unique-Id', guestUserId);
      return this.http.post<any>(cartsInfoUrl, newBody, {headers}).pipe(
        catchError(DeliveryDetailsService.handleError),
      );
    } else {
      return this.http.post<any>(cartsInfoUrl, newBody).pipe(
        take(1),
        map((response: any) => response),
        catchError(DeliveryDetailsService.handleError),
      );
    }
  }

  getCompanyUsers(): Observable<any> {
    const companyUsersUrl: string = this.glueUrl + '/company-users/mine';

    return this.http.get<any>(companyUsersUrl, this.httpParamsForGetCompanyUsersRoleItems)
      .pipe(
        tap(),
        catchError(DeliveryDetailsService.handleError),
      );
  }

  getCompanyUsersForApprove(): Observable<any> {
    const companyUsersUrl: string = this.glueUrl + '/company-approvers';

    return this.http.get<any>(companyUsersUrl, this.httpParamsForGetApprovers).pipe(
      tap(),
      catchError(DeliveryDetailsService.handleError),
    );
  }

  getAddressesFromData(addressesToAdd: IAddressesDataIncluded[] | ICustomAddressData[], addressList: IAddressData[]): IAddressData[] {
    addressesToAdd.forEach((data: IAddressesDataIncluded | ICustomAddressData) => {
      if (data?.attributes) {
        const newAddress: IAddressData = {
          id: data.id,
          name: AddressUtils.createAddressString(data.attributes),
          value: data.attributes,
        };
        const addressFound: IAddressData | undefined = addressList.find((address: IAddressData) => address.name === newAddress.name);
        if (!addressFound) {
          addressList.push(newAddress);
        }
      }
    });
    return addressList;
  }
}
